import React, { useState, useEffect, useCallback } from "react";
import { apiKeyService } from "../services/api";
import { Key, Plus, Trash2, Eye, EyeOff, TrendingUp, ChevronDown, ChevronUp, CheckSquare, Square, Pencil, Clock } from "lucide-react";

// ─── Model catalog grouped by provider ────────────────────────────────────────
const PROVIDER_CATALOG = [
  {
    provider: "Anthropic",
    models: [
      { id: "anthropic.claude-opus-4-20250514-v1:0",     name: "Claude Opus 4",        tier: "premium",  inputPer1M: 15.00,  outputPer1M: 75.00 },
      { id: "anthropic.claude-sonnet-4-5-20251101-v1:0", name: "Claude Sonnet 4.5",    tier: "standard", inputPer1M: 3.00,   outputPer1M: 15.00 },
      { id: "anthropic.claude-3-7-sonnet-20250219-v1:0", name: "Claude 3.7 Sonnet",    tier: "standard", inputPer1M: 3.00,   outputPer1M: 15.00 },
      { id: "anthropic.claude-3-5-sonnet-20241022-v2:0", name: "Claude 3.5 Sonnet v2", tier: "standard", inputPer1M: 3.00,   outputPer1M: 15.00 },
      { id: "anthropic.claude-3-5-sonnet-20240620-v1:0", name: "Claude 3.5 Sonnet v1", tier: "standard", inputPer1M: 3.00,   outputPer1M: 15.00 },
      { id: "anthropic.claude-3-5-haiku-20241022-v1:0",  name: "Claude 3.5 Haiku",     tier: "fast",     inputPer1M: 0.80,   outputPer1M: 4.00  },
      { id: "anthropic.claude-3-opus-20240229-v1:0",     name: "Claude 3 Opus",        tier: "premium",  inputPer1M: 15.00,  outputPer1M: 75.00 },
      { id: "anthropic.claude-3-haiku-20240307-v1:0",    name: "Claude 3 Haiku",       tier: "fast",     inputPer1M: 0.25,   outputPer1M: 1.25  },
      { id: "anthropic.claude-v2:1",                     name: "Claude 2.1",           tier: "legacy",   inputPer1M: 8.00,   outputPer1M: 24.00 },
      { id: "anthropic.claude-instant-v1",               name: "Claude Instant",       tier: "legacy",   inputPer1M: 0.80,   outputPer1M: 2.40  },
    ],
  },
  {
    provider: "Amazon",
    models: [
      { id: "amazon.nova-pro-v1:0",           name: "Nova Pro",            tier: "standard", inputPer1M: 0.80,  outputPer1M: 3.20  },
      { id: "amazon.nova-lite-v1:0",          name: "Nova Lite",           tier: "fast",     inputPer1M: 0.06,  outputPer1M: 0.24  },
      { id: "amazon.nova-micro-v1:0",         name: "Nova Micro",          tier: "fast",     inputPer1M: 0.035, outputPer1M: 0.14  },
      { id: "amazon.titan-text-premier-v1:0", name: "Titan Text Premier",  tier: "standard", inputPer1M: 0.50,  outputPer1M: 1.50  },
      { id: "amazon.titan-text-express-v1",   name: "Titan Text Express",  tier: "legacy",   inputPer1M: 0.20,  outputPer1M: 0.60  },
      { id: "amazon.titan-text-lite-v1",      name: "Titan Text Lite",     tier: "legacy",   inputPer1M: 0.15,  outputPer1M: 0.20  },
    ],
  },
  {
    provider: "Meta",
    models: [
      { id: "meta.llama3-3-70b-instruct-v1:0",  name: "Llama 3.3 70B",     tier: "standard", inputPer1M: 0.72,  outputPer1M: 0.72  },
      { id: "meta.llama3-2-90b-instruct-v1:0",  name: "Llama 3.2 90B",     tier: "standard", inputPer1M: 0.72,  outputPer1M: 0.72  },
      { id: "meta.llama3-2-11b-instruct-v1:0",  name: "Llama 3.2 11B",     tier: "fast",     inputPer1M: 0.16,  outputPer1M: 0.16  },
      { id: "meta.llama3-2-3b-instruct-v1:0",   name: "Llama 3.2 3B",      tier: "fast",     inputPer1M: 0.06,  outputPer1M: 0.06  },
      { id: "meta.llama3-1-405b-instruct-v1:0", name: "Llama 3.1 405B",    tier: "premium",  inputPer1M: 5.32,  outputPer1M: 16.00 },
      { id: "meta.llama3-1-70b-instruct-v1:0",  name: "Llama 3.1 70B",     tier: "standard", inputPer1M: 0.99,  outputPer1M: 0.99  },
      { id: "meta.llama3-1-8b-instruct-v1:0",   name: "Llama 3.1 8B",      tier: "fast",     inputPer1M: 0.22,  outputPer1M: 0.22  },
    ],
  },
  {
    provider: "Mistral AI",
    models: [
      { id: "mistral.mistral-large-2407-v1:0",     name: "Mistral Large 2407", tier: "standard", inputPer1M: 3.00, outputPer1M: 9.00 },
      { id: "mistral.mistral-large-2402-v1:0",     name: "Mistral Large 2402", tier: "standard", inputPer1M: 4.00, outputPer1M: 12.00 },
      { id: "mistral.mixtral-8x7b-instruct-v0:1",  name: "Mixtral 8x7B",       tier: "fast",     inputPer1M: 0.45, outputPer1M: 0.70 },
      { id: "mistral.mistral-7b-instruct-v0:2",    name: "Mistral 7B",         tier: "fast",     inputPer1M: 0.15, outputPer1M: 0.20 },
    ],
  },
  {
    provider: "Cohere",
    models: [
      { id: "cohere.command-r-plus-v1:0",      name: "Command R+",     tier: "standard", inputPer1M: 3.00, outputPer1M: 15.00 },
      { id: "cohere.command-r-v1:0",           name: "Command R",      tier: "fast",     inputPer1M: 0.50, outputPer1M: 1.50 },
      { id: "cohere.command-light-text-v14",   name: "Command Light",  tier: "legacy",   inputPer1M: 0.30, outputPer1M: 0.60 },
    ],
  },
  {
    provider: "AI21 Labs",
    models: [
      { id: "ai21.jamba-1-5-large-v1:0", name: "Jamba 1.5 Large", tier: "standard", inputPer1M: 2.00, outputPer1M: 8.00 },
      { id: "ai21.jamba-1-5-mini-v1:0",  name: "Jamba 1.5 Mini",  tier: "fast",     inputPer1M: 0.20, outputPer1M: 0.40 },
    ],
  },
  {
    provider: "GLM (z.ai)",
    models: [
      { id: "glm-4-plus",      name: "GLM-4 Plus",      tier: "standard", inputPer1M: 0.14,  outputPer1M: 0.14  },
      { id: "glm-4-flash",     name: "GLM-4 Flash",     tier: "fast",     inputPer1M: 0.015, outputPer1M: 0.015 },
      { id: "glm-4-air",       name: "GLM-4 Air",       tier: "fast",     inputPer1M: 0.105, outputPer1M: 0.105 },
      { id: "glm-z1-preview",  name: "GLM-Z1 Preview",  tier: "premium",  inputPer1M: 0.210, outputPer1M: 0.210 },
    ],
  },
];

const ALL_MODELS = PROVIDER_CATALOG.flatMap(p => p.models.map(m => ({ ...m, provider: p.provider })));

const TIER_STYLES = {
  premium:  "bg-purple-100 text-purple-700",
  standard: "bg-blue-100 text-blue-700",
  fast:     "bg-green-100 text-green-700",
  legacy:   "bg-gray-100 text-gray-600",
};

function getModelLabel(modelId) {
  const m = ALL_MODELS.find(x => x.id === modelId);
  return m ? `${m.provider} – ${m.name}` : modelId;
}

function usagePct(key) {
  if (!key.usage_limit || key.usage_limit === 0) return null;
  return Math.min(100, Math.round((key.current_usage / key.usage_limit) * 100));
}

function formatActivity(timestamp) {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}yr ago`;
}

function UsageBar({ pct }) {
  if (pct === null) return <span className="text-xs text-slate-400 font-medium">Unlimited</span>;
  const fill = pct >= 90
    ? 'linear-gradient(90deg,#ef4444,#f87171)'
    : pct >= 70
    ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
    : 'linear-gradient(90deg,#10b981,#34d399)';
  const text = pct >= 90 ? 'text-red-500' : pct >= 70 ? 'text-amber-500' : 'text-emerald-600';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: pct + '%', background: fill }} />
      </div>
      <span className={`text-xs font-medium tabular-nums ${text}`}>{pct}%</span>
    </div>
  );
}

// ─── Multi-select model chooser ───────────────────────────────────────────────
function ModelSelector({ selected, onChange }) {
  const [open, setOpen] = useState({});

  function toggle(modelId) {
    const next = selected.includes(modelId)
      ? selected.filter(id => id !== modelId)
      : [...selected, modelId];
    onChange(next);
  }

  function toggleProvider(provider) {
    const ids = PROVIDER_CATALOG.find(p => p.provider === provider).models.map(m => m.id);
    const allSelected = ids.every(id => selected.includes(id));
    if (allSelected) onChange(selected.filter(id => !ids.includes(id)));
    else onChange([...new Set([...selected, ...ids])]);
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
      {PROVIDER_CATALOG.map(({ provider, models }) => {
        const isOpen = open[provider] !== false;
        const providerIds = models.map(m => m.id);
        const allChecked = providerIds.every(id => selected.includes(id));
        const someChecked = providerIds.some(id => selected.includes(id));
        return (
          <div key={provider}>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 cursor-pointer select-none"
              onClick={() => setOpen(o => ({ ...o, [provider]: !isOpen }))}>
              <button type="button" className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                onClick={e => { e.stopPropagation(); toggleProvider(provider); }}>
                {allChecked ? <CheckSquare size={16} /> : someChecked
                  ? <CheckSquare size={16} className="opacity-50" />
                  : <Square size={16} />}
              </button>
              <span className="font-semibold text-sm text-gray-700 flex-1">{provider}</span>
              <span className="text-xs text-gray-400">{providerIds.filter(id => selected.includes(id)).length}/{models.length}</span>
              {isOpen ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
            </div>
            {isOpen && (
              <div className="divide-y divide-gray-50">
                {models.map(m => (
                  <label key={m.id} className="flex items-center gap-3 px-4 py-2 hover:bg-blue-50 cursor-pointer">
                    <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} className="accent-blue-600" />
                    <span className="flex-1 text-sm text-gray-800">{m.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${TIER_STYLES[m.tier]}`}>{m.tier}</span>
                    <span className="text-xs text-gray-400 tabular-nums">${m.inputPer1M}/1M in</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showValues, setShowValues] = useState({});

  // Generate modal
  const [showGenerate, setShowGenerate] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [selectedModels, setSelectedModels] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  // Edit modal (change models + limit on existing key)
  const [showEdit, setShowEdit] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editModels, setEditModels] = useState([]);
  const [editLimit, setEditLimit] = useState("");
  const [saving, setSaving] = useState(false);

  // Extend-limit modal
  const [showExtend, setShowExtend] = useState(false);
  const [extendKey, setExtendKey] = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [extending, setExtending] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiKeyService.getAll();
      setKeys(res.data?.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  // ── Generate ──────────────────────────────────────────────────────────────
  async function handleGenerate(e) {
    e.preventDefault();
    if (!keyName.trim()) return;
    setGenerating(true);
    try {
      const res = await apiKeyService.generate({
        keyName: keyName.trim(),
        usageLimit: usageLimit ? parseInt(usageLimit) : null,
        allowedModels: selectedModels.length > 0 ? selectedModels : null,
      });
      setNewKey(res.data?.data);
      await loadKeys();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  }

  function resetGenerate() {
    setShowGenerate(false);
    setKeyName("");
    setUsageLimit("");
    setSelectedModels([]);
    setNewKey(null);
  }

  // ── Edit existing key ──────────────────────────────────────────────────────
  function openEdit(key) {
    const current = key.allowed_models_parsed ||
      (key.allowed_models ? (() => { try { return JSON.parse(key.allowed_models); } catch(_) { return []; } })() : []);
    setEditKey(key);
    setEditModels(current);
    setEditLimit(key.usage_limit != null ? String(key.usage_limit) : "");
    setShowEdit(true);
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!editKey) return;
    setSaving(true);
    try {
      await apiKeyService.updateApiKey(editKey.key_id, {
        allowedModels: editModels.length > 0 ? editModels : null,
        usageLimit: editLimit !== "" ? parseInt(editLimit) : null,
      });
      setShowEdit(false);
      setEditKey(null);
      await loadKeys();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Extend limit ───────────────────────────────────────────────────────────
  function openExtend(key) {
    setExtendKey(key);
    setNewLimit(key.usage_limit ? String(key.usage_limit) : "");
    setShowExtend(true);
  }

  async function handleExtend(e) {
    e.preventDefault();
    if (!extendKey) return;
    setExtending(true);
    try {
      await apiKeyService.extendLimit(extendKey.key_id, parseInt(newLimit));
      setShowExtend(false);
      setExtendKey(null);
      await loadKeys();
    } catch (e) {
      setError(e.message);
    } finally {
      setExtending(false);
    }
  }

  async function handleDelete(keyId) {
    if (!window.confirm("Delete this API key?")) return;
    try {
      await apiKeyService.delete(keyId);
      await loadKeys();
    } catch (e) {
      setError(e.message);
    }
  }

  function toggleShowValue(keyId) {
    setShowValues(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  }

  if (loading) return <div className="p-8 text-center text-gray-500">Loading API keys…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-sm text-gray-500 mt-1">{keys.length} key{keys.length !== 1 ? "s" : ""} total</p>
        </div>
        <button onClick={() => setShowGenerate(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">
          <Plus size={16} /> Generate Key
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between">
          {error}
          <button onClick={() => setError(null)} className="ml-4 font-bold">×</button>
        </div>
      )}

      {/* Keys table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {keys.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Key size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No API keys yet</p>
            <p className="text-sm mt-1">Generate your first key to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Name", "Key", "Models", "Usage", "Limit", "Status", "Last Activity", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {keys.map(key => {
                  const models = key.allowed_models_parsed ||
                    (key.allowed_models ? (() => { try { return JSON.parse(key.allowed_models); } catch(_) { return null; } })() : null);
                  const pct = usagePct(key);
                  return (
                    <tr key={key.key_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{key.key_name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 max-w-xs">
                        <div className="flex items-center gap-2">
                          <span className="truncate">
                            {showValues[key.key_id] ? key.key_value : key.key_value?.replace(/./g, "•").slice(0, 24) + "…"}
                          </span>
                          <button onClick={() => toggleShowValue(key.key_id)} className="text-gray-400 hover:text-gray-700 flex-shrink-0">
                            {showValues[key.key_id] ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {models && models.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {models.slice(0, 3).map(id => {
                              const m = ALL_MODELS.find(x => x.id === id);
                              return (
                                <span key={id} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                                  {m ? m.name : id.split(".").pop()}
                                </span>
                              );
                            })}
                            {models.length > 3 && (
                              <span className="text-xs text-gray-400">+{models.length - 3} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">All models</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <span className="text-xs text-gray-600 tabular-nums">{(key.current_usage || 0).toLocaleString()} tokens</span>
                          <UsageBar pct={pct} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 tabular-nums">
                        {key.usage_limit ? key.usage_limit.toLocaleString() : "∞"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          key.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {key.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {key.last_activity ? (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Clock size={12} className="text-gray-400 flex-shrink-0" />
                            <span title={new Date(key.last_activity).toLocaleString()}>
                              {formatActivity(key.last_activity)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300 italic">Never used</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button title="Edit models & limit" onClick={() => openEdit(key)}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 rounded hover:bg-indigo-50">
                            <Pencil size={15} />
                          </button>
                          <button title="Extend token limit" onClick={() => openExtend(key)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50">
                            <TrendingUp size={15} />
                          </button>
                          <button title="Delete key" onClick={() => handleDelete(key.key_id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Generate modal ───────────────────────────────────────────────── */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-12 px-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-4">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Generate API Key</h2>
            </div>
            {newKey ? (
              <div className="p-6 space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm font-semibold text-green-800 mb-2">Key created — copy it now, it won't be shown again.</p>
                  <code className="block text-xs font-mono bg-white border border-green-200 rounded p-3 break-all select-all">
                    {newKey.keyValue}
                  </code>
                </div>
                {newKey.allowedModels && newKey.allowedModels.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Allowed models:</p>
                    <div className="flex flex-wrap gap-1">
                      {newKey.allowedModels.map(id => (
                        <span key={id} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{getModelLabel(id)}</span>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={resetGenerate} className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleGenerate} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key Name *</label>
                  <input value={keyName} onChange={e => setKeyName(e.target.value)} placeholder="e.g. Production App" required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Token Limit <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input type="number" value={usageLimit} onChange={e => setUsageLimit(e.target.value)} placeholder="Leave blank for unlimited" min="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Allowed Models</label>
                    <div className="flex gap-2 text-xs">
                      <button type="button" className="text-blue-600 hover:underline" onClick={() => setSelectedModels(ALL_MODELS.map(m => m.id))}>All</button>
                      <button type="button" className="text-gray-400 hover:underline" onClick={() => setSelectedModels([])}>None</button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    {selectedModels.length > 0 ? `${selectedModels.length} model${selectedModels.length !== 1 ? "s" : ""} selected` : "No restriction — key can use any model"}
                  </p>
                  <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                    <ModelSelector selected={selectedModels} onChange={setSelectedModels} />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={resetGenerate}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={generating}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {generating ? "Generating…" : "Generate Key"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Edit modal ───────────────────────────────────────────────────── */}
      {showEdit && editKey && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-12 px-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-4">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Edit Key</h2>
              <p className="text-sm text-gray-500 mt-0.5">{editKey.key_name}</p>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Token Limit</label>
                <input type="number" value={editLimit} onChange={e => setEditLimit(e.target.value)}
                  placeholder="Leave blank for unlimited" min="1"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <p className="text-xs text-gray-400 mt-1">
                  Current usage: <span className="font-medium tabular-nums">{(editKey.current_usage || 0).toLocaleString()}</span> tokens
                  {editKey.usage_limit ? ` / ${editKey.usage_limit.toLocaleString()} limit` : " (unlimited)"}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Allowed Models</label>
                  <div className="flex gap-2 text-xs">
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => setEditModels(ALL_MODELS.map(m => m.id))}>All</button>
                    <button type="button" className="text-gray-400 hover:underline" onClick={() => setEditModels([])}>None (unrestricted)</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  {editModels.length > 0 ? `${editModels.length} model${editModels.length !== 1 ? "s" : ""} selected` : "No restriction — key can use any model"}
                </p>
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                  <ModelSelector selected={editModels} onChange={setEditModels} />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEdit(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Extend-limit modal ───────────────────────────────────────────── */}
      {showExtend && extendKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Extend Token Limit</h2>
              <p className="text-sm text-gray-500 mt-1">{extendKey.key_name}</p>
            </div>
            <form onSubmit={handleExtend} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Current usage</p>
                  <p className="font-semibold tabular-nums">{(extendKey.current_usage || 0).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Current limit</p>
                  <p className="font-semibold tabular-nums">{extendKey.usage_limit ? extendKey.usage_limit.toLocaleString() : "∞"}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Limit</label>
                <input type="number" value={newLimit} onChange={e => setNewLimit(e.target.value)}
                  min={(extendKey.current_usage || 0) + 1} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                {newLimit && extendKey.usage_limit && (
                  <p className="text-xs text-green-600 mt-1">
                    +{(parseInt(newLimit) - extendKey.usage_limit).toLocaleString()} tokens added
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowExtend(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={extending}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {extending ? "Saving…" : "Update Limit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
